// Copyright 2025, University of Maryland, All Rights Reserved
import type { FC } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Database } from 'lucide-react'
import type { DatasourceConfigSectionProps, DatasourceType } from '../types'
import {
  DATASOURCE_TYPES,
  DATASOURCE_LABELS,
  DATASOURCE_DESCRIPTIONS,
} from '../constants'
import { AnalysisResultsConfig } from './AnalysisResultsConfig'
import { MarylandBillMasterListConfig } from './MarylandBillMasterListConfig'
import { FullTextConfig } from './FullTextConfig'
import { OriginalDocumentConfig } from './OriginalDocumentConfig'
import { OptixDocumentsConfig } from './OptixDocumentsConfig'

/**
 * Main datasource configuration section for pipeline nodes.
 *
 * Renders a dropdown to select datasource type, then shows the
 * appropriate configuration form for that type.
 */
export const DatasourceConfigSection: FC<DatasourceConfigSectionProps> = ({
  datasourceType,
  onDatasourceTypeChange,
  datasourceConfig,
  onConfigChange,
  allowedTypes,
  orgId,
  workspaceId,
  agentId,
  title = 'Data Source',
  description = 'Configure where documents are read from',
}) => {
  // Filter types if allowedTypes is provided
  const availableTypes = allowedTypes ?? DATASOURCE_TYPES

  // Create onChange handler that matches the expected signature
  const handleConfigChange = (key: string, value: unknown) => {
    onConfigChange(key, value)
  }

  // Render the appropriate config component based on type
  const renderConfigForm = () => {
    if (!datasourceType) {
      return (
        <p className="text-sm text-muted-foreground italic">
          Select a datasource type to configure settings.
        </p>
      )
    }

    switch (datasourceType) {
      case 'maryland_bill_master_list':
        return (
          <MarylandBillMasterListConfig
            config={datasourceConfig as any}
            onChange={handleConfigChange as any}
          />
        )
      case 'full_text':
        return (
          <FullTextConfig
            config={datasourceConfig as any}
            onChange={handleConfigChange as any}
          />
        )
      case 'original_document':
        return (
          <OriginalDocumentConfig
            config={datasourceConfig as any}
            onChange={handleConfigChange as any}
          />
        )
      case 'optix_documents':
        return (
          <OptixDocumentsConfig
            config={datasourceConfig as any}
            onChange={handleConfigChange as any}
            orgId={orgId}
            workspaceId={workspaceId}
          />
        )
      case 'analysis_results':
        return (
          <AnalysisResultsConfig
            config={datasourceConfig as any}
            onChange={handleConfigChange as any}
            orgId={orgId}
            workspaceId={workspaceId}
            agentId={agentId}
          />
        )
      default:
        return (
          <p className="text-sm text-muted-foreground">
            Unknown datasource type: {datasourceType}
          </p>
        )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Datasource Type Selector — hidden when only one type is available */}
        {availableTypes.length > 1 && (
          <div className="space-y-2">
            <Label htmlFor="datasource-type">Datasource Type</Label>
            <Select
              value={datasourceType ?? ''}
              onValueChange={(value) => onDatasourceTypeChange(value as DatasourceType)}
            >
              <SelectTrigger id="datasource-type" className="w-full">
                <SelectValue placeholder="Select datasource type" />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    <div className="flex flex-col">
                      <span>{DATASOURCE_LABELS[type]}</span>
                      <span className="text-xs text-muted-foreground">
                        {DATASOURCE_DESCRIPTIONS[type]}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Type-specific Configuration */}
        {renderConfigForm()}
      </CardContent>
    </Card>
  )
}
